import Error from 'next/error'
import { FC } from 'react'

import { faCalendarDays } from '@awesome.me/kit-e270131d0a/icons/classic/regular'
import {
  faClock,
  faBuilding,
  faUser,
  faPrint,
  faMoon,
  faSun,
} from '@awesome.me/kit-e270131d0a/icons/classic/regular'
import { Button, Icon, CardGrid, Text, Card } from '@saasalerts/ui'
import { ReportAccessKey } from '@/components/Modals'
import { ErrorCode } from '@/types/reports'
import {
  PartnerBrandingOptions,
  SCHEDULED_REPORT_BRANDING_DISPLAY,
} from '@saasalerts/common/shared'
import { getDomainBranding } from '@saasalerts/ui'
import { getThemeClass } from '@/utils/constants'
import {
  findChildByType,
  formatToUSDate,
  getReportById,
  hasDateRangeFilter,
} from '@/utils/methods'
import { useOptionalTheme } from '@/contexts/ThemeContext'
import { useLicenseScope } from '@/contexts/LicenseScopeContext'

// using a slots pattern here: https://medium.com/@srph/react-imitating-vue-slots-eab8393f96fd
// https://spectrum.chat/react/help/jsx-element-type-component-does-not-have-any-construct-or-call-signatures~033f76eb-6181-4d25-b95c-928b91203f31
interface ShareReportLayoutHeaderProps {
  reportId?: string
  organizationName?: string
  accountEmail?: string
  expiryDate?: string
  startDate?: string
  endDate?: string
  enablePrint?: boolean
  selectedBenchmarkName?: string
}

interface Component {
  Header: FC<React.PropsWithChildren<ShareReportLayoutHeaderProps>>
  Body: FC<React.PropsWithChildren<unknown>>
}

const Header: FC<React.PropsWithChildren<ShareReportLayoutHeaderProps>> = ({
  reportId,
  expiryDate,
  organizationName,
  startDate,
  endDate,
  accountEmail,
  enablePrint = true,
  selectedBenchmarkName,
}) => {
  const themeContext = useOptionalTheme()
  const handlePrint = themeContext?.handlePrint ?? (() => window.print())
  const reportName = getReportById(reportId ?? '')?.name
  return (
    <CardGrid columns={1} containerPadding={0} className="print:!hidden">
      <Card
        padding={false}
        noBorder
        className="dark:!bg-dark-bg"
        accessory={
          <Text className="pb-1">
            You can edit the report with the available options, You can then
            print/export the report. If the report expires, please ask your
            partner for another link.
          </Text>
        }
        accessoryPosition="body-top-left"
      >
        <Card.Header>
          <div className="pb-1 text-2xl font-medium leading-7">
            {reportName}
          </div>
        </Card.Header>

        <Card.Body>
          <CardGrid columns={1} gap={2} containerPadding={0}>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              {expiryDate && (
                <Text className="flex items-center">
                  <Icon
                    size="sm"
                    icon={faClock}
                    className="mr-1.5 size-4 shrink-0"
                    aria-hidden="true"
                  />
                  Expires On {formatToUSDate(expiryDate)}
                </Text>
              )}
              {organizationName && (
                <Text className="flex items-center">
                  <Icon
                    size="sm"
                    icon={faBuilding}
                    className="mr-1.5 size-4 shrink-0"
                    aria-hidden="true"
                  />
                  {organizationName}
                </Text>
              )}
              {accountEmail && (
                <Text className="flex items-center">
                  <Icon
                    size="sm"
                    icon={faUser}
                    className="mr-1.5 size-4 shrink-0"
                    aria-hidden="true"
                  />
                  {accountEmail}
                </Text>
              )}
              {hasDateRangeFilter(reportId) && (
                <Text className="flex items-center">
                  <Icon
                    size="sm"
                    icon={faCalendarDays}
                    className="mr-1.5 size-4 shrink-0"
                    aria-hidden="true"
                  />
                  {formatToUSDate(startDate)} - {formatToUSDate(endDate)}
                </Text>
              )}
              {selectedBenchmarkName && (
                <Text className="flex items-center">
                  <Icon
                    size="sm"
                    icon={faBuilding}
                    className="mr-1.5 size-4 shrink-0"
                    aria-hidden="true"
                  />
                  {selectedBenchmarkName}
                </Text>
              )}
            </div>
            {enablePrint && (
              <div className="flex gap-4">
                <Button Icon={faPrint} onClick={handlePrint}>
                  Print Report
                </Button>
              </div>
            )}
          </CardGrid>
        </Card.Body>
      </Card>
    </CardGrid>
  )
}

const Body: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  return <>{children}</>
}

export const ShareReportLayout: Component &
  FC<
    React.PropsWithChildren<{
      width?: 'normal' | 'full'
      error?: ErrorCode
      brandingOptions?: PartnerBrandingOptions
      children: React.ReactNode
    }>
  > = ({ children, error, brandingOptions }) => {
  const resolvedScope = useLicenseScope()

  const header = findChildByType(children, Header)
  const body = findChildByType(children, Body)
  const themeContext = useOptionalTheme()

  if (error === ErrorCode.PAGE_NOT_FOUND || error === ErrorCode.INVALID_TOKEN) {
    return <Error statusCode={404} />
  }

  if (error === ErrorCode.REQUIRE_AUTHENTICATION) {
    return <ReportAccessKey />
  }

  if (error === ErrorCode.INVALID_ACCESS_KEY) {
    return <ReportAccessKey invalidAccessKey />
  }

  const theme =
    brandingOptions?.display === SCHEDULED_REPORT_BRANDING_DISPLAY.SHOW
      ? getThemeClass(brandingOptions?.color)
      : ''

  const displaySaasAlertsCoBranding = !(
    brandingOptions?.reportOptions?.hideSaasAlertsCoBranding === true
  )

  const domainBranding = getDomainBranding(resolvedScope)!

  return (
    <div className={`${theme} dark:bg-dark-bg`}>
      <header className="relative z-0 flex h-14 items-center border-b border-white bg-brand-600 dark:border-gray-800 dark:bg-dark-surface print:hidden">
        <div className="flex h-14 w-60 items-center px-4 py-2 pr-0">
          <div className="inline-block">
            {brandingOptions?.display ===
              SCHEDULED_REPORT_BRANDING_DISPLAY.SHOW && (
              <img className="h-10" src={brandingOptions?.logo} alt="Logo" />
            )}
          </div>
        </div>
        <div className="ml-auto flex items-center">
          <div className="relative">
            <button
              onClick={themeContext?.toggleTheme}
              className="flex h-14 w-12 items-center justify-center rounded-none transition-colors hover:bg-white/10 active:bg-black/20"
            >
              <Icon
                icon={themeContext?.theme === 'dark' ? faSun : faMoon}
                className="size-5 !text-white dark:text-gray-200"
              />
            </button>
          </div>
        </div>
      </header>

      <div className="m-auto max-w-screen-2xl px-8 md:px-12 lg:px-16 print:px-0">
        <CardGrid columns={1} containerPadding={4}>
          {header}
          {body}
        </CardGrid>
        {displaySaasAlertsCoBranding && (
          <Text className="px-4 pb-4 print:hidden">
            Powered by {domainBranding.name}
          </Text>
        )}
        <div id="react-select-portal" className="print:hidden" />
      </div>
    </div>
  )
}

ShareReportLayout.displayName = 'ShareReportLayout'
ShareReportLayout.Header = Header
ShareReportLayout.Body = Body
